import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import {
  ChatMessage,
  ChatDocument,
  selectDocumentsLeftToAttach,
  selectConversation,
  selectCurrentRunID,
  selectMessages,
  selectAwaitingReply,
  selectRunSteps,
  selectUnsentDocuments,
  selectUploadingDocumentIDs,
} from "app/modules/aiAssistant/AIAssistantSlice";
import { deleteChatDocument, sendChatMessage, uploadFile } from "app/modules/aiAssistant/AIAssistantThunks";
import { greetingText } from "app/modules/aiAssistant/conversation/messages/emptyChatContent";

const greeting: ChatMessage = { content: greetingText, isUserAuthor: false };

// Shared by every view, so the page can stop a reply asked for in the pop-up
const stopReplyByRunID = new Map<string, () => void>();

// The open chat, shared by the Assistimo page and the pop-up
export default function useConversation() {
  const thunkDispatch = useThunkDispatch();
  const [draft, setDraft] = useState("");
  const currentRunID = useSelector(selectCurrentRunID);
  const conversation = useSelector(selectConversation);
  const storedMessages = useSelector(selectMessages);
  const isAwaitingReply = useSelector(selectAwaitingReply);
  const runSteps = useSelector(selectRunSteps);
  const documents = useSelector(selectUnsentDocuments);
  const documentsLeftToAttach = useSelector(selectDocumentsLeftToAttach);
  const uploadingDocumentIDs = useSelector(selectUploadingDocumentIDs);
  const isUploading = uploadingDocumentIDs.length > 0;

  const messages = useMemo(() => [greeting, ...storedMessages], [storedMessages]);

  const sendMessage = async (messageText: string) => {
    if (messageText.trim() === "" || isUploading) return;

    const pendingReply = thunkDispatch(sendChatMessage({ conversation, message: messageText }));
    stopReplyByRunID.set(pendingReply.requestId, pendingReply.abort);
    const result = await pendingReply;
    stopReplyByRunID.delete(pendingReply.requestId);

    // Give a stopped or failed question back, unless they've already started typing another
    if (sendChatMessage.rejected.match(result)) {
      setDraft((currentDraft) => currentDraft || messageText);
    }
  };

  return {
    messages,
    draft,
    setDraft,
    sendMessage,
    stopReply: () => currentRunID && stopReplyByRunID.get(currentRunID)?.(),
    isAwaitingReply,
    runSteps,
    documents,
    documentsLeftToAttach,
    uploadingDocumentIDs,
    isUploading,
    attachFiles: (files: File[]) => files.forEach((file) => thunkDispatch(uploadFile({ conversation, file }))),
    removeDocument: (document: ChatDocument) => thunkDispatch(deleteChatDocument(document.id)),
  };
}
